import { ActionIcon, Box, Container, Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { ArrowLeft, Clock, Layers, Users } from 'react-feather';

/**
 * MED-1085 — reusable page header for full-content (non-immersive) pages.
 *
 * Generalized from the MED-1040 post-session header: a full-width white band
 * with a bottom border, an optional back button, a bold title, a right-aligned
 * accessory slot (phase indicator — e.g. a "Session Complete" badge, a timing
 * badge, or live timers), and a metadata band (program/cohort · client ·
 * duration) with leading icons. The band is full-bleed; an inner
 * `Container size="xl"` keeps its content aligned with the page body.
 *
 * `sticky` is an opt-in capability — off by default (static, as today); when on,
 * the band pins to the top of the scroll container.
 *
 * The richer universal-header contract (`Session {N}: {Topic}` identity, group
 * name in the band) lands later (MED-1085 follow-up, blocked on the MED-1103
 * topic field). It slots into `title` / the metadata props without changing this
 * component's shape.
 */
export interface FullContentHeaderProps {
  /** Title / identity slot (string or node). */
  title: ReactNode;
  /** When provided, renders a leading back button that calls this. */
  onBack?: () => void;
  /** Program or cohort name (Layers icon). */
  programName?: string | null;
  /** Client / organization name (Users icon). */
  clientName?: string | null;
  /** Session duration in minutes (Clock icon). */
  durationMinutes?: number | null;
  /** Right-aligned phase accessory (badge / timers / status). */
  accessory?: ReactNode;
  /**
   * Where the accessory sits:
   *  - 'inline' (default) — beside the title, on the title row.
   *  - 'band' — pinned to the far right and vertically centered against the
   *    full header band (title + metadata rows), e.g. the coach prep RSVP.
   */
  accessoryPlacement?: 'inline' | 'band';
  /** Opt-in: pin the band to the top of the scroll container. Default false. */
  sticky?: boolean;
  'data-testid'?: string;
}

const TITLE_COLOR = '#1A1A1A';
const META_COLOR = '#888888';
const BAND_BORDER = '#E5E7EB';

export function FullContentHeader({
  title,
  onBack,
  programName,
  clientName,
  durationMinutes,
  accessory,
  accessoryPlacement = 'inline',
  sticky = false,
  ...rest
}: FullContentHeaderProps) {
  const inlineAccessory = accessoryPlacement === 'inline';

  const stack = (
    <Stack gap={10}>
      <Group align="center" wrap="nowrap" gap="sm">
        {onBack && (
          <ActionIcon
            variant="default"
            color="gray"
            size={38}
            radius="md"
            onClick={onBack}
            aria-label="Back"
            data-testid="full-content-header-back"
          >
            <ArrowLeft size={18} />
          </ActionIcon>
        )}
        {typeof title === 'string' ? (
          <Text fz={24} fw={700} c={TITLE_COLOR} lh={1.1}>
            {title}
          </Text>
        ) : (
          title
        )}
        {inlineAccessory && accessory}
      </Group>
      {(programName || clientName || durationMinutes != null) && (
        <Group gap={16} align="center">
          {/* MED-1092 — nudge the metadata icons up 2px to optically center
              them against the text (matches the in-session header treatment). */}
          {programName && (
            <Group gap={6} align="center" wrap="nowrap">
              <Layers
                size={14}
                color={META_COLOR}
                aria-hidden="true"
                style={{ transform: 'translateY(-2px)' }}
              />
              <Text fz={13} c={META_COLOR}>
                {programName}
              </Text>
            </Group>
          )}
          {clientName && (
            <Group gap={6} align="center" wrap="nowrap">
              <Users
                size={14}
                color={META_COLOR}
                aria-hidden="true"
                style={{ transform: 'translateY(-2px)' }}
              />
              <Text fz={13} c={META_COLOR}>
                {clientName}
              </Text>
            </Group>
          )}
          {durationMinutes != null && (
            <Group gap={6} align="center" wrap="nowrap">
              <Clock
                size={14}
                color={META_COLOR}
                aria-hidden="true"
                style={{ transform: 'translateY(-2px)' }}
              />
              <Text fz={13} c={META_COLOR}>
                {durationMinutes} minutes
              </Text>
            </Group>
          )}
        </Group>
      )}
    </Stack>
  );

  return (
    <Box
      data-testid={rest['data-testid']}
      style={{
        backgroundColor: '#FFFFFF',
        borderBottom: `1px solid ${BAND_BORDER}`,
        // When sticky, pin just below the layout's AppShell header (the fixed
        // top bar) using Mantine's header-height var; falls back to 0 outside
        // an AppShell.
        ...(sticky
          ? {
              position: 'sticky',
              top: 'var(--app-shell-header-height, 0px)',
              zIndex: 100,
            }
          : {}),
      }}
    >
      <Container size="xl" pt="lg" pb="md">
        {!inlineAccessory && accessory ? (
          // 'band' placement: accessory pinned far-right, vertically centered
          // against the full band (title + metadata) via align="center".
          <Group justify="space-between" align="center" wrap="nowrap">
            <Box style={{ flex: 1, minWidth: 0 }}>{stack}</Box>
            {accessory}
          </Group>
        ) : (
          stack
        )}
      </Container>
    </Box>
  );
}

export default FullContentHeader;
